/**
 * Subscription Selector
 * Handles purchase type selection (one-time vs subscription) and selling plan updates
 */
(function () {
  if (window.SubscriptionSelector && window.initSubscriptionSelectors) {
    window.initSubscriptionSelectors();
    return;
  }

class SubscriptionSelector {
  constructor(container) {
    this.container = container;
    this.style = container.dataset.style || 'cards';
    this.productId = container.dataset.productId;
    this.sellingPlanInput = container.querySelector('[data-selling-plan-input]');
    this.variantInput = container.querySelector('[data-variant-input]');
    this.benefits = container.querySelector('[data-subscription-benefits]');
    this.addToCartBtn = container.querySelector('[data-subscription-add-to-cart]');

    // Price elements
    this.onetimePriceEl = container.querySelector('[data-onetime-price]');
    this.subscriptionPriceEl = container.querySelector('[data-subscription-price]');
    this.originalPriceEl = container.querySelector('[data-original-price]');
    this.discountBadgeEl = container.querySelector('[data-discount-badge]');
    this.savingsEl = container.querySelector('[data-savings]');
    this.savingsAmountEl = container.querySelector('[data-savings-amount]');
    this.savingsContainerEl = container.querySelector('[data-savings-container]');

    // Discount info from data attributes
    this.discountPercent = parseInt(container.dataset.discountPercent, 10) || 0;
    this.moneyFormat = container.dataset.moneyFormat || '';
    this.locale = container.dataset.locale || document.documentElement.lang || undefined;
    this.currencyCode = container.dataset.currencyCode || window.Shopify?.currency?.active || 'EUR';
    this.currentOnetimePrice = null; // Will be set on first update

    // Current selection type
    this.currentType = container.dataset.default || 'subscription';

    this.bindEvents();
    this.bindExternalEvents();
  }

  bindExternalEvents() {
    // Listen for external variant changes (e.g., from flyout)
    this.container.addEventListener('subscription:external-variant-change', (e) => {
      const { variantId, price, pricesByPlan, quantity } = e.detail;
      if (price != null && variantId) {
        if (Number.isInteger(quantity) && quantity > 0) this.container.dataset.quantity = quantity;
        this.updateVariantPrice(price, variantId, pricesByPlan);
      }
    });
  }

  bindEvents() {
    if (this.style === 'cards') {
      this.bindCardsEvents();
    } else {
      this.bindCompactEvents();
    }

    // Add to cart button
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  bindCardsEvents() {
    // Option selection (cards)
    const options = this.container.querySelectorAll('.subscription-option');
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        // Don't trigger if clicking frequency buttons
        if (e.target.closest('.subscription-frequency-btn')) return;

        this.selectOption(option.dataset.optionType);
      });
    });

    // Frequency buttons
    const frequencyBtns = this.container.querySelectorAll('.subscription-frequency-btn');
    frequencyBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectFrequency(btn);
      });
    });
  }

  bindCompactEvents() {
    // Tab selection (compact)
    const tabs = this.container.querySelectorAll('.subscription-compact__tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.selectOption(tab.dataset.optionType);
      });
    });

    // Frequency dropdown
    const frequencySelect = this.container.querySelector('[data-frequency-select]');
    if (frequencySelect) {
      frequencySelect.addEventListener('change', () => {
        const selectedOption = frequencySelect.options[frequencySelect.selectedIndex];
        this.updateSellingPlan(frequencySelect.value);
        this.updatePriceDisplay(selectedOption.dataset);
      });
    }
  }

  selectOption(type) {
    const isSubscription = type === 'subscription';
    this.currentType = type;

    if (this.style === 'cards') {
      // Update card selection
      const options = this.container.querySelectorAll('.subscription-option');
      options.forEach(option => {
        const isSelected = option.dataset.optionType === type;
        option.classList.toggle('subscription-option--selected', isSelected);
        option.querySelector('input[type="radio"]').checked = isSelected;
      });

      // Get first selling plan ID if subscription
      if (isSubscription) {
        const activeBtn = this.container.querySelector('.subscription-frequency-btn--active');
        if (activeBtn) {
          this.updateSellingPlan(activeBtn.dataset.sellingPlanId);
          this.updatePriceDisplay(activeBtn.dataset);
        }
      } else {
        this.updateSellingPlan('');
      }
    } else {
      // Update tab selection (compact)
      const tabs = this.container.querySelectorAll('.subscription-compact__tab');
      tabs.forEach(tab => {
        const isSelected = tab.dataset.optionType === type;
        tab.classList.toggle('subscription-compact__tab--selected', isSelected);
        tab.setAttribute('aria-pressed', String(isSelected));
      });

      // Show/hide frequency dropdown
      const frequencyContainer = this.container.querySelector('[data-compact-frequency]');
      if (frequencyContainer) {
        frequencyContainer.classList.toggle('subscription-compact__frequency--visible', isSubscription);
      }

      // Show/hide savings
      if (this.savingsContainerEl) {
        this.savingsContainerEl.classList.toggle('subscription-compact__savings--visible', isSubscription);
      }

      // Update selling plan
      if (isSubscription) {
        const select = this.container.querySelector('[data-frequency-select]');
        if (select) {
          const selectedOption = select.options[select.selectedIndex];
          this.updateSellingPlan(select.value);
          this.updatePriceDisplay(selectedOption.dataset);
        }
      } else {
        this.updateSellingPlan('');
      }
    }

    // Show/hide benefits
    if (this.benefits) {
      this.benefits.classList.toggle('subscription-benefits--visible', isSubscription);
    }

    // Update button text
    this.updateButtonText(type);

    // Dispatch custom event
    this.container.dispatchEvent(new CustomEvent('subscription:change', {
      bubbles: true,
      detail: {
        type: type,
        sellingPlanId: this.sellingPlanInput?.value || null
      }
    }));
  }

  selectFrequency(button) {
    // Update active state
    const buttons = this.container.querySelectorAll('.subscription-frequency-btn');
    buttons.forEach(btn => {
      const isActive = btn === button;
      btn.classList.toggle('subscription-frequency-btn--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    // Update selling plan
    this.updateSellingPlan(button.dataset.sellingPlanId);

    // Update price display
    this.updatePriceDisplay(button.dataset);
  }

  updateButtonText(type) {
    if (!this.addToCartBtn) return;

    const textOnetime = this.addToCartBtn.dataset.textOnetime;
    const textSubscribe = this.addToCartBtn.dataset.textSubscribe;
    const iconOnetime = this.addToCartBtn.dataset.iconOnetime;
    const iconSubscribe = this.addToCartBtn.dataset.iconSubscribe;

    // Update text - prefer [data-button-text] element if present
    const textEl = this.addToCartBtn.querySelector('[data-button-text]');
    if (textEl) {
      textEl.textContent = type === 'subscription' ? textSubscribe : textOnetime;
    } else {
      this.addToCartBtn.textContent = type === 'subscription' ? textSubscribe : textOnetime;
    }

    // Update icon if icons are defined and icon element exists
    const iconEl = this.addToCartBtn.querySelector('[data-button-icon]');
    if (iconEl && iconOnetime && iconSubscribe) {
      const iconName = type === 'subscription' ? iconSubscribe : iconOnetime;
      // Use icon snippet - fetch from assets or inline SVGs
      this.updateButtonIcon(iconEl, iconName);
    }

    // Update button price
    if (type === 'subscription' && this.subscriptionPriceEl) {
      this.updateButtonPrice(this.subscriptionPriceEl.textContent);
    } else if (this.onetimePriceEl) {
      this.updateButtonPrice(this.onetimePriceEl.textContent);
    }
  }

  updateButtonPrice(formattedPrice) {
    if (!this.addToCartBtn) return;
    const priceEl = this.addToCartBtn.querySelector('[data-button-price]');
    if (priceEl && formattedPrice) {
      priceEl.textContent = formattedPrice;
    }
  }

  updateButtonIcon(iconEl, iconName) {
    // Icon SVGs mapped here for JS-based updates
    const icons = {
      'cart': '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5039 2C13.9149 2.00014 15.8804 4.00334 15.9062 6.47461H16.1201C18.8113 6.47461 20.9999 8.71955 21 11.4795V16.9961C20.9997 19.7549 18.8111 22 16.1201 22H6.87988C4.18984 22 2.00026 19.7549 2 16.9961V11.4795C2.00006 8.71955 4.18972 6.47461 6.87988 6.47461H7.07812C7.09504 5.26464 7.55609 4.14915 8.38184 3.31055C9.21781 2.45971 10.3406 2.035 11.5039 2ZM6.87988 7.97461C4.99555 7.97466 3.46198 9.54657 3.46191 11.4795V16.9961C3.46217 18.9278 4.99567 20.5 6.87988 20.5H16.1211C18.0052 20.4998 19.5388 18.9278 19.5391 16.9961V11.4795C19.539 9.54665 18.0053 7.97478 16.1211 7.97461H15.9072V9.55566C15.9072 9.96966 15.5792 10.3057 15.1758 10.3057C14.7725 10.3055 14.4453 9.96958 14.4453 9.55566V7.97461H8.53906V9.55566C8.53906 9.96966 8.211 10.3057 7.80762 10.3057C7.40437 10.3055 7.07715 9.96956 7.07715 9.55566V7.97461H6.87988ZM11.501 3.5C10.7109 3.528 9.97039 3.80812 9.41113 4.37598C8.85981 4.93688 8.5525 5.68052 8.54102 6.47461H14.4443C14.4186 4.83036 13.109 3.50013 11.501 3.5Z" fill="currentColor"/></svg>',
      'subscribe': '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 10C16.5 13.59 13.59 16.5 10 16.5C8.11 16.5 6.4 15.7 5.2 14.4L6.6 13C7.5 14 8.7 14.5 10 14.5C12.49 14.5 14.5 12.49 14.5 10H12L15.5 6.5L19 10H16.5Z" fill="currentColor"/><path d="M3.5 10C3.5 6.41 6.41 3.5 10 3.5C11.89 3.5 13.6 4.3 14.8 5.6L13.4 7C12.5 6 11.3 5.5 10 5.5C7.51 5.5 5.5 7.51 5.5 10H8L4.5 13.5L1 10H3.5Z" fill="currentColor"/></svg>',
      'preorder': '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM10 16C6.69 16 4 13.31 4 10C4 6.69 6.69 4 10 4C13.31 4 16 6.69 16 10C16 13.31 13.31 16 10 16Z" fill="currentColor"/><path d="M10.5 6H9V11L13.25 13.52L14 12.24L10.5 10.15V6Z" fill="currentColor"/></svg>',
      'backorder': '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.5 4H4.5C3.67 4 3 4.67 3 5.5V14.5C3 15.33 3.67 16 4.5 16H15.5C16.33 16 17 15.33 17 14.5V5.5C17 4.67 16.33 4 15.5 4ZM15 14H5V6H15V14Z" fill="currentColor"/><path d="M7 8H13V10H7V8Z" fill="currentColor"/><path d="M10 11L7 14H13L10 11Z" fill="currentColor"/></svg>'
    };

    if (icons[iconName]) {
      iconEl.innerHTML = icons[iconName];
    }
  }

  updatePriceDisplay(planData) {
    if (!planData) return;

    const {
      planPriceFormatted,
      planDiscount,
      planSavingsFormatted,
      planSavings
    } = planData;

    // Update subscription price
    if (this.subscriptionPriceEl && planPriceFormatted) {
      this.subscriptionPriceEl.textContent = planPriceFormatted;
    }

    // Update discount badge
    if (this.discountBadgeEl) {
      const discount = parseInt(planDiscount, 10);
      if (discount > 0) {
        this.discountBadgeEl.textContent = `-${discount}%`;
        this.discountBadgeEl.style.display = '';
      } else {
        this.discountBadgeEl.style.display = 'none';
      }
    }

    // Update savings display
    const savings = parseInt(planSavings, 10);
    if (this.savingsAmountEl && planSavingsFormatted) {
      this.savingsAmountEl.textContent = planSavingsFormatted;
    }

    // Show/hide savings and original price based on whether there are savings
    if (this.savingsEl) {
      this.savingsEl.style.display = savings > 0 ? '' : 'none';
    }
    if (this.originalPriceEl) {
      this.originalPriceEl.style.display = savings > 0 ? '' : 'none';
    }
    if (this.savingsContainerEl) {
      this.savingsContainerEl.classList.toggle('subscription-compact__savings--visible', savings > 0);
    }

    // Update button price if subscription is active
    if (this.currentType === 'subscription' && planPriceFormatted) {
      this.updateButtonPrice(planPriceFormatted);
    }

    // Dispatch price change event
    this.container.dispatchEvent(new CustomEvent('subscription:price-change', {
      bubbles: true,
      detail: {
        price: planData.planPrice,
        priceFormatted: planPriceFormatted,
        discount: planDiscount,
        savings: planSavings,
        savingsFormatted: planSavingsFormatted
      }
    }));
  }

  /**
   * Update prices when variant changes (called from flyout)
   * @param {number} newPrice - New variant price in cents
   * @param {number} variantId - New variant ID
   */
  updateVariantPrice(newPrice, variantId, pricesByPlan) {
    if (!Number.isFinite(Number(newPrice)) || Number(newPrice) < 0) return;

    this.currentOnetimePrice = newPrice;

    // Update variant input
    if (this.variantInput && variantId) {
      this.variantInput.value = variantId;
      this.container.dataset.variantId = variantId;
    }

    // Update onetime price
    if (this.onetimePriceEl) {
      this.onetimePriceEl.textContent = this.formatMoney(newPrice);
    }

    // Calculate and update subscription price based on discount
    const priceForPlan = (id, discount) => {
      if (pricesByPlan && Object.prototype.hasOwnProperty.call(pricesByPlan, id)) {
        const total = Number(pricesByPlan[id]);
        if (Number.isFinite(total) && total >= 0) return total;
      }
      // Compatibility for standalone single-quantity consumers without a controller.
      return Math.round(newPrice * (100 - discount) / 100);
    };
    const frequencySelect = this.container.querySelector('[data-frequency-select]');
    const activeFrequency = this.container.querySelector('.subscription-frequency-btn--active')
      || (frequencySelect && frequencySelect.options[frequencySelect.selectedIndex]);
    const activeId = activeFrequency && (activeFrequency.dataset.sellingPlanId || activeFrequency.value);
    const activeDiscount = activeFrequency ? Number(activeFrequency.dataset.planDiscount) || 0 : this.discountPercent;
    const subscriptionPrice = priceForPlan(activeId, activeDiscount);
    const savings = newPrice - subscriptionPrice;

    if (this.subscriptionPriceEl) {
      this.subscriptionPriceEl.textContent = this.formatMoney(subscriptionPrice);
    }

    if (this.originalPriceEl) {
      this.originalPriceEl.textContent = this.formatMoney(newPrice);
      this.originalPriceEl.style.display = savings > 0 ? '' : 'none';
    }

    if (this.savingsAmountEl) {
      this.savingsAmountEl.textContent = this.formatMoney(savings);
    }

    if (this.savingsEl) {
      this.savingsEl.style.display = savings > 0 ? '' : 'none';
    }

    // Update frequency buttons with new prices
    const frequencyBtns = this.container.querySelectorAll('.subscription-frequency-btn, [data-frequency-select] option');
    frequencyBtns.forEach(btn => {
      const btnDiscount = parseInt(btn.dataset.planDiscount, 10) || 0;
      const btnPrice = priceForPlan(btn.dataset.sellingPlanId || btn.value, btnDiscount);
      const btnSavings = newPrice - btnPrice;

      btn.dataset.planPrice = btnPrice;
      btn.dataset.planPriceFormatted = this.formatMoney(btnPrice);
      btn.dataset.planSavings = btnSavings;
      btn.dataset.planSavingsFormatted = this.formatMoney(btnSavings);
    });

    // If a frequency is currently active, update displayed prices to match
    if (activeFrequency) {
      this.updatePriceDisplay(activeFrequency.dataset);
    }

    // Update button price
    if (this.currentType === 'subscription') {
      this.updateButtonPrice(this.formatMoney(subscriptionPrice));
    } else {
      this.updateButtonPrice(this.formatMoney(newPrice));
    }

    // Dispatch event for external listeners
    this.container.dispatchEvent(new CustomEvent('subscription:variant-price-change', {
      bubbles: true,
      detail: {
        variantId: variantId,
        onetimePrice: newPrice,
        subscriptionPrice: subscriptionPrice,
        discountPercent: this.discountPercent,
        savings: savings
      }
    }));

  }

  formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, this.moneyFormat);
    }

    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currencyCode
      }).format(amount / 100);
    } catch {
      return (amount / 100).toFixed(2);
    }
  }

  updateSellingPlan(planId) {
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = planId || '';
    }

    // Dispatch custom event
    this.container.dispatchEvent(new CustomEvent('subscription:plan-change', {
      bubbles: true,
      detail: {
        sellingPlanId: planId || null
      }
    }));
  }

  async addToCart() {
    if (!this.variantInput || !this.addToCartBtn || this.addPending) return;
    this.addPending = true;

    const variantId = this.variantInput.value;
    const sellingPlanId = this.sellingPlanInput?.value || null;
    const purchaseType = this.currentType;

    // Get quantity from data attribute (defaults to 1 if unset)
    const quantity = parseInt(this.container.dataset.quantity, 10) || 1;

    // Prepare cart data
    const cartData = {
      id: variantId,
      quantity: quantity
    };

    if (sellingPlanId) {
      cartData.selling_plan = sellingPlanId;
    }

    // Update button state
    const textEl = this.addToCartBtn.querySelector('[data-button-text]');
    const originalText = textEl ? textEl.textContent : this.addToCartBtn.textContent;
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.classList.add('subscription-selector__button--loading');

    try {
      if (!window.CartAdd) throw new Error('Cart controls are not ready');
      const root = window.Shopify?.routes?.root || '/';
      const result = await window.CartAdd.add(root + 'cart/add.js', { items: [cartData] }, this.addToCartBtn);

      // Success state
      if (textEl) {
        textEl.textContent = '✓';
      } else {
        this.addToCartBtn.textContent = '✓';
      }
      this.addToCartBtn.classList.add('subscription-selector__button--success');

      // Dispatch success event
      this.container.dispatchEvent(new CustomEvent('subscription:added-to-cart', {
        bubbles: true,
        detail: {
          item: result,
          type: purchaseType,
          sellingPlanId: sellingPlanId,
          quantity: quantity
        }
      }));

      // Open cart drawer after short delay
      setTimeout(() => {
        // Reset button state for next time
        if (textEl) {
          textEl.textContent = originalText;
        } else {
          this.addToCartBtn.textContent = originalText;
        }
        this.addToCartBtn.classList.remove('subscription-selector__button--success');
        this.addToCartBtn.classList.remove('subscription-selector__button--loading');
        this.addToCartBtn.disabled = !!window.CartOperations?.isBlocked();
      }, 600);

    } catch (error) {
      console.error('Add to cart error:', error);
      let status = this.container.querySelector('[data-subscription-cart-status]');
      if (!status) {
        status = document.createElement('p');
        status.dataset.subscriptionCartStatus = '';
        status.setAttribute('role', 'alert');
        this.addToCartBtn.insertAdjacentElement('afterend', status);
      }
      if (!window.CartAdd?.showRecovery?.(status)) {
        status.textContent = error.data?.description || error.data?.message || error.message;
      }

      // Error state
      if (textEl) {
        textEl.textContent = '✗';
      } else {
        this.addToCartBtn.textContent = '✗';
      }
      this.addToCartBtn.classList.add('subscription-selector__button--error');

      // Reset button after delay
      setTimeout(() => {
        if (textEl) {
          textEl.textContent = originalText;
        } else {
          this.addToCartBtn.textContent = originalText;
        }
        this.addToCartBtn.classList.remove('subscription-selector__button--error');
        this.addToCartBtn.classList.remove('subscription-selector__button--loading');
        this.addToCartBtn.disabled = !!window.CartOperations?.isBlocked();
      }, 2000);
    } finally {
      this.addPending = false;
    }
  }

  openCartDrawer() {
    // Dispatch cart:add event - this triggers cart drawer to refresh and open
    // The cart-drawer.liquid listens for this event and fetches fresh cart data
    document.dispatchEvent(new CustomEvent('cart:add', { detail: { trigger: this.addToCartBtn } }));
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count elements
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });

      // Dispatch cart updated event — payload shape matches cart-drawer's
      // refreshDrawer(): listeners read e.detail.item_count etc. directly.
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }
}

// Initialize all subscription selectors
function initSubscriptionSelectors() {
  document.querySelectorAll('[data-subscription-selector]').forEach(container => {
    if (!container.dataset.initialized) {
      const instance = new SubscriptionSelector(container);
      // Store instance reference on container for external access (e.g., from flyout)
      container.subscriptionInstance = instance;
      container.dataset.initialized = 'true';

      // Dispatch event so flyout can update prices
      container.dispatchEvent(new CustomEvent('subscription:initialized', {
        bubbles: true,
        detail: { instance }
      }));
    }
  });
}

window.SubscriptionSelector = SubscriptionSelector;
window.initSubscriptionSelectors = initSubscriptionSelectors;

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubscriptionSelectors);
} else {
  initSubscriptionSelectors();
}

// Re-init when Shopify sections are loaded/updated
document.addEventListener('shopify:section:load', initSubscriptionSelectors);
})();
